/**
 * 台面上"这块数据是什么成色"的统一说法。
 *
 * 控制台有三种不同的空/假,长得像但含义完全不同,混着画就是骗人
 * (design-fleet-console-implementation.md §3「诚实优先」):
 *
 *   <NoDataSource>  hub 根本还没有这个源(告警/认知/运维)。不画假数字,说明第几期接。
 *   <DemoData>      有 UI 没接线,显示的是原型 mock。必须**明着标**,不能悄悄混进真页面。
 *   <LoadError>     有源但读失败。这条绝不能降级成上面两种 —— 那会把"后端挂了"
 *                   画成"一切正常,只是没有数据"(§9b)。
 */

export function NoDataSource({ title, reason, phase }: { title: string; reason: string; phase?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 mb-4">
        <i className="ri-database-2-line text-2xl text-foreground-300" />
      </span>
      <p className="text-sm font-medium text-foreground-600">{title}</p>
      <p className="text-xs text-foreground-400 mt-2 max-w-md leading-relaxed">{reason}</p>
      {phase && (
        <span className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-[11px] text-foreground-500 font-mono">
          <i className="ri-road-map-line text-xs" />{phase}
        </span>
      )}
    </div>
  );
}

export function DemoData({ what, why }: { what: string; why: string }) {
  return (
    <div className="flex items-start gap-2.5 mb-4 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200/70">
      <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-px">
        <i className="ri-flask-line text-sm text-amber-600" />
      </span>
      <p className="text-[11px] text-amber-800 leading-relaxed">
        <strong className="font-semibold">演示数据</strong> · {what}
        <span className="text-amber-700/80"> —— {why}</span>
      </p>
    </div>
  );
}

export function LoadError({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200/70">
      <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-px">
        <i className="ri-error-warning-line text-sm text-red-500" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-red-700 leading-relaxed">
          <strong className="font-semibold">读取失败</strong> —— {error}
        </p>
        <p className="text-[10px] text-red-600/70 mt-0.5">这不是"没有数据":读不到时台面不显示任何数值。</p>
      </div>
      {onRetry && (
        <button onClick={onRetry}
          className="shrink-0 text-[11px] text-red-600 hover:text-red-700 underline underline-offset-2 cursor-pointer whitespace-nowrap">
          重试
        </button>
      )}
    </div>
  );
}

/** 一个"暂无数据源"的小角标,给卡片/图表这种放不下整段说明的地方。 */
export function NoSourceTag({ hint }: { hint: string }) {
  return (
    <span title={hint}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-background-100 text-[10px] text-foreground-400 font-mono">
      <i className="ri-subtract-line text-[10px]" />暂无数据源
    </span>
  );
}
